import { EMAIL_PREVIEW_WIDTH } from 'lib';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Empty, Icons, Note, Segmented, T } from './ui';
import { Link } from 'react-router-dom';

/**
 * Renders the compiled email, in sandboxed iframes.
 *
 * The iframes are sandboxed with no `allow-scripts` and no `allow-same-origin`. The HTML comes from
 * the server and contains administrator-entered text, and while the renderer escapes it, a preview
 * pane is not the place to rely on a single layer - a sandboxed frame cannot reach the CMS session
 * even if something got through.
 *
 * `srcDoc` rather than a URL because there is no document to fetch: the preview is rendered on
 * demand and never stored.
 */
/*
  Two views, not three.

  `both` rendered a 390px and a 680px email side by side, which was worth having while this pane
  had a page column to itself. In the builder's right-hand half it has around 500px, so two-up
  meant each email wrapping onto its own row at roughly half scale — the same two frames, stacked,
  smaller, and a scroll apart. The toggle does that better.
*/
/**
 * Display names for the locales the banner is written in.
 *
 * A code an operator has not seen before falls through to its own uppercase form rather than to
 * nothing, so an unlabelled language is still selectable the day one is added on the server.
 */
const LOCALE_LABELS = {
  en: 'EN',
  ja: 'JA'
};

const OPTIONS = [
  { value: 'desktop', label: 'Desktop' },
  { value: 'mobile', label: 'Mobile' }
];

/**
 * The factor that makes a design-width email fit the space it has been given.
 *
 * Returns 1 whenever there is room, and never more than 1 — an email shown larger than it sends is
 * as misleading as one shown clipped, and the mail clients this has to survive do not enlarge it.
 *
 * `clientWidth` of 0 means the element has not been laid out yet, which is every render under
 * jsdom: left alone rather than treated as "no room at all", so the tests that assert the design
 * width still read it.
 */
const useFitScale = (designWidth) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => {
      const available = element.clientWidth;
      if (!available) return;
      setScale(Math.min(1, available / designWidth));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    /*
      Observed rather than read once on mount. The pane is a grid column that changes width when
      the window does, when the two-up view wraps, and when the scheduler below it grows — and a
      scale measured once is wrong from the first of those onwards.
    */
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [designWidth]);

  return [ref, scale];
};

/**
 * One rendered email: the mat, the sandboxed frame inside it, and the caption over both.
 *
 * ## Why this scales now, having deliberately refused to
 *
 * The frame renders at its true design width and is then transformed down to fit. The previous
 * decision here was the opposite — render at full size and let the mat scroll — and the reasoning
 * was sound as far as it went: the hero bar's type is 11px, close enough to the floor several mail
 * clients enforce that *whether it is legible* is itself one of the things being checked, and a
 * shrunken preview answers that question at the wrong size.
 *
 * What changed is the pane. It sits beside the campaign builder now rather than in a column of its
 * own, so on any ordinary laptop a 680px email has around 500px to live in — and "scroll instead
 * of scale" at that ratio means the operator sees two thirds of an email and has to drag sideways
 * to see the rest of every single row. That is not a more honest preview, it is an unreadable one,
 * and the composition is the thing this pane is mostly used to check.
 *
 * The original objection is answered rather than overruled: the caption states the percentage
 * whenever the frame is not at true size, so nobody reads 11px type at 70% and concludes it is
 * fine. Widen the window and it returns to 100%, and says so by saying nothing.
 *
 * Nothing is cut off at any scale, which is the property the scrolling version was protecting and
 * the `flex: 1` version broke: `mj-body` is a fixed 680px, it does not reflow, and the
 * `@playaz4playaz` handle at the far right of the hero row is the first thing to vanish when a
 * frame is merely narrowed.
 */
/*
  The rendered email cut down to its banner: the hero and its bar, and nothing else.

  Cut from the real render rather than drawn by a banner-only endpoint, so the Banner page still
  previews the wording by the same path a send resolves it — locale, fallback and all. Every
  element that is not the banner, or on the way down to it, is removed: the preheader, the
  greeting, the body, the button, the footer and the open marker. The card's bottom padding goes
  too, so the card closes on the bar. Anything without a banner is returned as it came.
*/
const BANNER_SELECTORS = ['.p4p-hero', '.p4p-hero-bar-section'];

export function bannerOnlyHtml(html) {
  if (!html || typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const keep = new Set(
    BANNER_SELECTORS.map((selector) => doc.querySelector(selector)).filter(Boolean)
  );
  if (!keep.size) return html;

  const onPath = new Set();
  keep.forEach((node) => {
    for (let at = node; at && at !== doc.body; at = at.parentNode) onPath.add(at);
  });

  const prune = (node) =>
    Array.from(node.childNodes).forEach((child) => {
      if (keep.has(child)) return;
      if (onPath.has(child)) prune(child);
      else child.remove();
    });
  prune(doc.body);

  const style = doc.createElement('style');
  style.textContent = '.p4p-card > table > tbody > tr > td { padding-bottom: 0 !important; }';
  doc.head.appendChild(style);

  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

/*
  How tall the banner-only frames are drawn, at the email's own width.

  Measured off the welcome render: the banner with the page's top margin is 269px at desktop and
  156px at mobile. The rest is room for the longest wording the limits allow to wrap a line further.
*/
const BANNER_HEIGHT = { desktop: 300, mobile: 190 };

const Frame = ({ html, width, height, label, fit }) => {
  const [ref, scale] = useFitScale(width);
  const scaled = scale < 0.999;

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        /*
          Basis at the design width, capped there, free to shrink below it. The cap is what stops
          a wide pane blowing a 390px mobile email up to fill it; the shrink is what hands the
          desktop email to the scale below rather than letting it overflow the card.
        */
        flex: `1 1 ${width}px`,
        maxWidth: width,
        minWidth: 0
      }}
    >
      {scaled ? (
        <span style={{ fontSize: 11.5, fontWeight: 700, color: T.muted, letterSpacing: '0.05em' }}>
          {label.toUpperCase()} - {width}px
          {scaled ? (
            <span style={{ fontWeight: 600, color: T.faint }}>
              {` \u00b7 shown at ${Math.round(scale * 100)}%`}
            </span>
          ) : null}
        </span>
      ) : null}

      {/*
        The mat behind the email, and the page inside it, both from the scale.

        `#EDF0F4` and `#FFFFFF` were written here directly. The white matters: an email is rendered
        against a mail client's own white, so the frame must not tint it — `T.card` is that white,
        and saying so is what keeps a later change to the card colour from silently repainting
        every preview.

        The mat takes the *scaled* size while the frame inside keeps the real one. A transform does
        not affect layout, so without this the mat would still reserve the full 680px and leave a
        third of itself blank beside a shrunken email.
      */}
      <div
        style={{
          borderRadius: 10,
          overflow: 'hidden',
          background: T.borderSoft,
          width: Math.round(width * scale),
          // A fitted frame shrinks with its content; a full email keeps the card's height instead.
          height: fit ? Math.round(height * scale) : height,
          maxWidth: '100%'
        }}
      >
        <iframe
          title={`${label} email preview`}
          srcDoc={withPreviewLinks(html)}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          style={{
            width,
            /*
              Taller as it shrinks, so the card keeps its height and the smaller type buys more of
              the email rather than more empty mat. At 70% that is half an email again in view.
            */
            height: fit ? height : Math.round(height / scale),
            border: 0,
            display: 'block',
            background: T.card,
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }}
        />
      </div>
    </div>
  );
};

/**
 * The email as the preview frame shows it: every real link opens in a new tab.
 *
 * QA: a detail row linked to the campaign's destination did nothing when clicked, and neither did
 * the button. The frame's sandbox allowed nothing at all, so no click in it could go anywhere. It
 * now allows new tabs (still no scripts, still no access to this page), and a `<base>` sends every
 * link to one, including the linked row, whose anchor the renderer writes without a target.
 *
 * The preview's placeholders (`#preview-unsubscribe`, `#preview-view`) stay in the frame: they have
 * no address to open, and a new tab for each would be an empty page.
 */
export function withPreviewLinks(html) {
  if (!html) return html;

  const base = '<base target="_blank">';
  const withBase = /<head[^>]*>/i.test(html)
    ? html.replace(/<head[^>]*>/i, (head) => `${head}${base}`)
    : `${base}${html}`;

  return withBase.replace(/<a\b[^>]*>/gi, (anchor) => {
    if (!/\bhref="#/i.test(anchor)) return anchor;
    const untargeted = anchor.replace(/\starget="[^"]*"/gi, '');
    return untargeted.replace(/^<a\b/i, '<a target="_self"');
  });
}

/** The view a variant names, or Desktop for anything this pane does not draw. */
const viewFor = (variant) => (OPTIONS.some((o) => o.value === variant) ? variant : 'desktop');

const PreviewPane = ({
  mobile,
  desktop,
  loading,
  variant,
  variantRequest,
  compact,
  unrecognisedTags,
  /*
    The banner's language, and a way to change it.

    **This is a viewing control and nothing else.** It picks which language's banner is drawn in
    the frame; it is not a campaign field, it is not read by save or send, and switching it changes
    nothing about who receives what. The shell holds it beside `view` rather than on the campaign
    draft, which is what keeps that true structurally rather than by everyone remembering.

    Absent means the pane renders without the switch, which is what a caller that has no opinion
    about language should get.
  */
  locale,
  locales,
  onLocaleChange,
  /** Where the banner's wording is edited. Absent hides the affordance. */
  bannerHref,
  /*
    Show only the banner. For the Banner page, where the wording is all that can change and the
    welcome email's greeting and body below it are only in the way.
  */
  bannerOnly
}) => {
  /*
    Three states, not a boolean.

    This held `both: true|false` and mapped false to Mobile, so picking **Desktop** showed the
    mobile frame - the control offered three options and could only express two.

    ## Why `variant` now moves the view, having deliberately only seeded it

    The previous decision was that `variant` seeds rather than controls, and its reasoning was
    right: "a controlled prop would fight them on every re-render", so an operator's own toggling
    would be undone by any render the shell happened to do. That is still true and is still
    honoured below — nothing here overwrites `view` while the request is unchanged.

    What the seeding could not do is the thing the editor's buttons exist for. **Preview Mobile**
    sets `?variant=mobile` and this pane, already mounted, ignored it. The button did nothing, twice
    over: the first press changed a parameter nobody read, and a second press did not even change
    the parameter.

    So the *press* is the event, not the value. `variantRequest` increments on every press, which is
    what makes a repeat press work — asking for Mobile again after toggling to Desktop is a real
    request and has to be answered. `variant` keeps carrying the choice in the URL so it survives a
    reload; `variantRequest` is memory-only, because a request is a thing that just happened and
    must not be replayed by opening a shared link.

    Adjusted during render rather than in an effect: this is React's own pattern for a value derived
    from props, and it avoids painting one frame at the old width before correcting it.
  */
  const [view, setView] = useState(() => viewFor(variant));
  const lastRequest = useRef(variantRequest);

  if (variantRequest !== lastRequest.current) {
    lastRequest.current = variantRequest;
    const requested = viewFor(variant);
    if (requested !== view) setView(requested);
  }

  /*
    Two loading states, because there are two situations and they want opposite treatment.

    **Nothing rendered yet** replaces the pane, as it always did. There is nothing to preserve and a
    silent gap is the worst answer.

    **Something rendered, newer render in flight** must not. The preview follows typing now, so this
    case arrives every few seconds while an operator writes — and blanking a whole email to say
    "rendering" would make the pane flash on every pause. Worse, it would hide the thing being
    edited at exactly the moment the operator looked up to check it. So the previous email stays,
    and the caption says it is catching up.
  */
  const hasRendered = !!(mobile || desktop);
  const refreshing = loading && hasRendered;

  /*
    Labelled from the locale codes the server sent rather than from a list written here.

    The allow-list lives in one place on the backend, and duplicating it as a CMS constant is how
    the two drift: the day a third language is approved, a hardcoded pair here would silently keep
    offering two.
  */
  const localeOptions = (locales ?? []).map((code) => ({
    value: code,
    label: LOCALE_LABELS[code] ?? code.toUpperCase()
  }));

  if (loading && !hasRendered) {
    return (
      // Announced: a preview that takes a moment is otherwise silence to a screen-reader user, who
      // has no way to tell "still working" from "nothing happened".
      <div
        role="status"
        aria-live="polite"
        style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}
      >
        Rendering the template...
      </div>
    );
  }

  if (!mobile && !desktop) {
    return (
      <div style={{ padding: compact ? 24 : 12 }}>
        <Empty title="Nothing to preview yet">
          Pick a template, or press Refresh preview. The preview is built from the same components
          the real email uses, so what shows here is what sends.
        </Empty>
      </div>
    );
  }

  /** The height the mat reserves, whatever the scale. */
  const height = compact ? 420 : 660;

  /*
    The pane insets itself, rather than sitting flush inside the card's border.

    `compact` used to be the only case with any padding, and the builder does not pass it - so on
    the page this pane actually lives on, the toggle, the mat and the two notes all ran into the
    card's own border with nothing between them. Padding belongs here rather than on `PreviewCard`
    because the meta strip above it is a full-bleed row with its own rule underneath, and a padded
    card would inset that too.
  */
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: compact ? 14 : 18
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10
        }}
      >
        {/*
          Not announced, unlike the first render above.

          `aria-live` here would interrupt a screen-reader user mid-sentence every time they paused
          typing, to tell them something they did not ask about. The first render is worth
          announcing because the pane is empty; this one is not, because the email they were reading
          is still there.
        */}
        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.faint }}>
          {refreshing ? 'Updating…' : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {localeOptions.length > 1 ? (
            <Segmented
              options={localeOptions}
              value={locale}
              onChange={onLocaleChange}
              label="Banner language"
            />
          ) : null}
          <Segmented options={OPTIONS} value={view} onChange={setView} label="Preview width" />
          {/*
            A real link, not a button that navigates.

            An operator who wants the composer left open middle-clicks or cmd-clicks this, and only
            an anchor gives them that. It also carries its destination in its accessible name — an
            unlabelled glyph is a control you have to press to find out what it does, which on a
            page with a Send button is not a game worth offering.
          */}
          {bannerHref ? (
            <Link
              to={bannerHref}
              aria-label="Edit banner wording"
              title="Edit banner wording"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: T.faint,
                padding: 4,
                borderRadius: 6,
                lineHeight: 0
              }}
            >
              <Icons.External width={14} height={14} aria-hidden="true" />
            </Link>
          ) : null}
        </div>
      </div>

      {/*
        Centred, because the email no longer fills the row it is in.

        The frame is capped at its design width and the pane is wider than that on most screens, so
        left-aligning it left the slack as one lopsided gutter down the right-hand side - the email
        read as pushed out of its card rather than sat in it. `flex-grow` cannot take up that slack
        (`maxWidth` is the whole point of the cap), so `justify-content` splits it in two.
      */}
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        {view === 'mobile' ? (
          <Frame
            html={bannerOnly ? bannerOnlyHtml(mobile) : mobile}
            width={EMAIL_PREVIEW_WIDTH.MOBILE}
            height={bannerOnly ? BANNER_HEIGHT.mobile : height}
            label="Mobile"
            fit={bannerOnly}
          />
        ) : (
          <Frame
            html={bannerOnly ? bannerOnlyHtml(desktop) : desktop}
            width={EMAIL_PREVIEW_WIDTH.DESKTOP}
            height={bannerOnly ? BANNER_HEIGHT.desktop : height}
            label="Desktop"
            fit={bannerOnly}
          />
        )}
      </div>

      {/* The banner carries no fan data, so the sample-data caveat has nothing to qualify there. */}
      {bannerOnly ? null : (
        <Note>
          Previewed with sparse sample data on purpose - many fans have no team, rank or card on
          record, so this shows what most of the audience actually receives. The values shown are
          samples, not one fan's real data.
        </Note>
      )}

      {/*
        Tags the renderer does not recognise.

        They survive interpolation and print literally, which is the right behaviour - deleting a
        typo would hide it. But on screen an unrecognised tag looks exactly like one that worked,
        and the only place the difference showed up was a fan's inbox.
      */}
      {unrecognisedTags?.length ? (
        <Note style={{ color: T.red }}>
          <strong>Not recognised:</strong> {unrecognisedTags.map((tag) => `{${tag}}`).join(', ')}.
          These will print exactly as written. Check the spelling against the variable list.
        </Note>
      ) : null}
    </div>
  );
};

export default PreviewPane;
